/**
 * AdminCRMPanel — Advanced CRM with rich timeline, pipeline Kanban,
 * follow-up automations, communication templates, and activity insights.
 * Sprint 10: Full rewrite — richer UX, automation alerts, templates.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { logWarn } from "../shared/logger.js";
import { RADIUS, TYPE, SP, TRANSITION } from "../shared/theme.js";
import { flexRow, flexBetween, flexCol, cardBase } from "../shared/styles.js";
import { Badge } from "./adminUtils.jsx";

// ── Constants ────────────────────────────────────────────
const PIPELINE_STAGES = [
  { id: "lead", label: "Lead", color: "#64748B", icon: "🎯" },
  { id: "contato", label: "Contato", color: "#0891B2", icon: "📞" },
  { id: "proposta", label: "Proposta", color: "#7C3AED", icon: "📋" },
  { id: "onboarding", label: "Onboarding", color: "#2563EB", icon: "🚀" },
  { id: "ativo", label: "Cliente Ativo", color: "#16A34A", icon: "✅" },
];

const PERIOD_OPTS = [
  { label: "Todos", value: "all" },
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
  { label: "90d", value: "90d" },
  { label: "12m", value: "12m" },
];

const EVENT_TYPES = {
  reuniao: { icon: "📅", label: "Reunião", color: "#2563EB" },
  contato: { icon: "📞", label: "Contato", color: "#0891B2" },
  email: { icon: "📧", label: "Email", color: "#7C3AED" },
  nota: { icon: "📝", label: "Nota", color: "#64748B" },
  alerta: { icon: "🔔", label: "Alerta", color: "#DC2626" },
  aporte: { icon: "💰", label: "Aporte", color: "#16A34A" },
  suitability: { icon: "📋", label: "Suitability", color: "#D97706" },
  snapshot: { icon: "📊", label: "Snapshot", color: "#0D9488" },
};

const MSG_TEMPLATES = [
  {
    id: "review",
    label: "📋 Revisão de Carteira",
    subject: "Revisão periódica de carteira",
    body: "Olá {nome}, gostaria de agendar sua revisão periódica de carteira. Quando seria um bom horário para conversarmos?",
  },
  {
    id: "welcome",
    label: "👋 Boas-vindas",
    subject: "Bem-vindo à LPA Wealth",
    body: "Olá {nome}, seja muito bem-vindo(a) à LPA Wealth! Estou à disposição para conversarmos sobre seus objetivos financeiros.",
  },
  {
    id: "followup",
    label: "🔄 Follow-up",
    subject: "Acompanhamento",
    body: "Olá {nome}, dando seguimento à nossa última conversa. Tem alguma dúvida ou gostaria de agendar uma nova reunião?",
  },
  {
    id: "report",
    label: "📊 Relatório Mensal",
    subject: "Relatório de performance mensal",
    body: "Olá {nome}, segue seu relatório mensal de performance. Qualquer dúvida, estou à disposição.",
  },
];

// ── Helpers ────────────────────────────────────────────
function inferStage(client, events) {
  const clientEvents = events[client.client_id] || [];
  const hasSnapshot = clientEvents.some((e) => (e.tipo || e.tipo_evento) === "snapshot");
  const hasAporte = clientEvents.some((e) => (e.tipo || e.tipo_evento) === "aporte");
  if (client.onboarding_done && (hasSnapshot || hasAporte)) return "ativo";
  if (client.onboarding_done) return "onboarding";
  const hasContact = clientEvents.some(
    (e) => (e.tipo || e.tipo_evento) === "contato" || (e.tipo || e.tipo_evento) === "reuniao"
  );
  const hasSuitability = clientEvents.some((e) => (e.tipo || e.tipo_evento) === "suitability");
  if (hasSuitability) return "proposta";
  if (hasContact) return "contato";
  return "lead";
}

function loadPipelineOverrides() {
  try {
    return JSON.parse(localStorage.getItem("crm_pipeline_stages") || "{}");
  } catch {
    return {};
  }
}
function savePipelineOverrides(map) {
  try {
    localStorage.setItem("crm_pipeline_stages", JSON.stringify(map));
  } catch {
    /* quota exceeded */
  }
}

function daysAgo(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return Infinity;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function fmtDatePT(dateStr) {
  if (!dateStr) return "–";
  try {
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return "–";
  }
}

function relativeDate(dateStr) {
  const days = daysAgo(dateStr);
  if (days === Infinity) return "–";
  if (days === 0) return "Hoje";
  if (days === 1) return "Ontem";
  if (days < 7) return `${days}d atrás`;
  if (days < 30) return `${Math.floor(days / 7)}sem atrás`;
  if (days < 365) return `${Math.floor(days / 30)}m atrás`;
  return `${Math.floor(days / 365)}a atrás`;
}

// ── Main Component ───────────────────────────────────────
export default function AdminCRMPanel({
  clients = [],
  crmEvents: propEvents,
  onInsertCRMEvent,
  T,
}) {
  const [view, setView] = useState("pipeline");
  const [selectedClient, setSelectedClient] = useState(null);
  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [evtTypeFilter, setEvtTypeFilter] = useState("all");
  const [evtPeriodFilter, setEvtPeriodFilter] = useState("all");
  const [pipelineOverrides, setPipelineOverrides] = useState(loadPipelineOverrides);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvt, setNewEvt] = useState({
    tipo: "reuniao",
    descricao: "",
    data: new Date().toISOString().split("T")[0],
  });

  // Use prop events if available, otherwise fetch independently
  useEffect(() => {
    if (propEvents && Object.keys(propEvents).length > 0) {
      setEvents(propEvents);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { fetchAllCRMEvents } = await import("../services/crm.ts");
        const map = await fetchAllCRMEvents();
        if (!cancelled) setEvents(map);
      } catch (err) {
        logWarn("AdminCRMPanel", err.message, { context: "load CRM events" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propEvents]);

  // ── Client stage resolution ──
  const getStage = useCallback(
    (client) => pipelineOverrides[client.client_id] || inferStage(client, events),
    [pipelineOverrides, events]
  );

  const moveClient = useCallback((clientId, newStage) => {
    setPipelineOverrides((prev) => {
      const next = { ...prev, [clientId]: newStage };
      savePipelineOverrides(next);
      return next;
    });
  }, []);

  // ── Pipeline columns ──
  const pipelineColumns = useMemo(() => {
    const cols = {};
    PIPELINE_STAGES.forEach((s) => {
      cols[s.id] = [];
    });
    clients.forEach((c) => {
      const stage = getStage(c);
      if (cols[stage]) cols[stage].push(c);
      else cols.lead.push(c);
    });
    return cols;
  }, [clients, getStage]);

  // ── Automation alerts ──
  const automationAlerts = useMemo(() => {
    const alerts = [];
    const now = Date.now();
    clients.forEach((c) => {
      const cEvents = events[c.client_id] || [];
      const lastContact = cEvents.find(
        (e) =>
          (e.tipo || e.tipo_evento) === "contato" ||
          (e.tipo || e.tipo_evento) === "reuniao" ||
          (e.tipo || e.tipo_evento) === "email"
      );
      const daysSince = lastContact
        ? Math.floor(
            (now -
              new Date(
                lastContact.event_date || lastContact.data_evento || lastContact.data
              ).getTime()) /
              86400000
          )
        : Infinity;

      if (daysSince > 90) {
        alerts.push({
          type: "inactivity",
          severity: "critical",
          client: c,
          message: daysSince === Infinity ? "Sem contato registrado" : `${daysSince}d sem contato`,
          action: "Agendar reunião",
        });
      } else if (daysSince > 60) {
        alerts.push({
          type: "follow_up",
          severity: "warning",
          client: c,
          message: `${daysSince}d desde último contato`,
          action: "Enviar follow-up",
        });
      }
    });
    return alerts.sort((a, _b) => (a.severity === "critical" ? -1 : 1));
  }, [clients, events]);

  // ── Filtered clients ──
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase().trim();
    return clients.filter(
      (c) =>
        (c.full_name || "").toLowerCase().includes(q) ||
        (c.client_id || "").toLowerCase().includes(q)
    );
  }, [clients, clientSearch]);

  // ── Filtered events for selected client ──
  const filteredEvents = useMemo(() => {
    if (!selectedClient) return [];
    let evts = events[selectedClient.client_id] || [];
    if (evtTypeFilter !== "all") {
      evts = evts.filter((e) => (e.tipo || e.tipo_evento) === evtTypeFilter);
    }
    if (evtPeriodFilter !== "all") {
      const now = Date.now();
      const days = { "7d": 7, "30d": 30, "90d": 90, "12m": 365 }[evtPeriodFilter] || 0;
      if (days > 0) {
        const cutoff = now - days * 86400000;
        evts = evts.filter(
          (e) => new Date(e.event_date || e.data_evento || e.data).getTime() >= cutoff
        );
      }
    }
    return evts;
  }, [selectedClient, events, evtTypeFilter, evtPeriodFilter]);

  // ── Activity stats for selected client ──
  const clientStats = useMemo(() => {
    if (!selectedClient) return null;
    const cEvents = events[selectedClient.client_id] || [];
    const typeCounts = {};
    cEvents.forEach((e) => {
      const t = e.tipo || e.tipo_evento || "nota";
      typeCounts[t] = (typeCounts[t] || 0) + 1;
    });
    const lastEvent = cEvents[0];
    const firstEvent = cEvents[cEvents.length - 1];
    return {
      total: cEvents.length,
      typeCounts,
      lastEventDate: lastEvent?.event_date || lastEvent?.data_evento || lastEvent?.data,
      firstEventDate: firstEvent?.event_date || firstEvent?.data_evento || firstEvent?.data,
      daysSinceLastContact: daysAgo(
        lastEvent?.event_date || lastEvent?.data_evento || lastEvent?.data
      ),
    };
  }, [selectedClient, events]);

  // ── Add event handler ──
  const addEvent = async () => {
    if (!selectedClient || !newEvt.descricao.trim()) return;
    const key = selectedClient.client_id;
    setLoading(true);
    const payload = {
      client_id: key,
      tipo_evento: newEvt.tipo,
      descricao: newEvt.descricao,
      data_evento: newEvt.data,
    };
    try {
      if (onInsertCRMEvent) {
        await onInsertCRMEvent(payload);
      } else {
        const { insertCRMEvent } = await import("../services/crm.ts");
        await insertCRMEvent(payload);
      }
      // Optimistic update
      setEvents((prev) => ({
        ...prev,
        [key]: [
          {
            id: `temp_${Date.now()}`,
            ...payload,
            tipo: newEvt.tipo,
            data: newEvt.data,
            created_at: new Date().toISOString(),
          },
          ...(prev[key] || []),
        ],
      }));
    } catch (err) {
      logWarn("AdminCRMPanel", err.message, { context: "insert CRM event" });
      // Fallback optimistic
      setEvents((prev) => ({
        ...prev,
        [key]: [
          {
            id: `local_${Date.now()}`,
            tipo: newEvt.tipo,
            descricao: newEvt.descricao,
            data: newEvt.data,
          },
          ...(prev[key] || []),
        ],
      }));
    }
    setLoading(false);
    setNewEvt({ tipo: "reuniao", descricao: "", data: new Date().toISOString().split("T")[0] });
    setShowAddEvent(false);
  };

  // ── Template action ──
  const applyTemplate = (template) => {
    if (!selectedClient) return;
    const name = selectedClient.full_name || selectedClient.client_id;
    setNewEvt({
      tipo: "email",
      descricao: `[${template.subject}] ${template.body.replace("{nome}", name)}`,
      data: new Date().toISOString().split("T")[0],
    });
    setShowTemplates(false);
    setShowAddEvent(true);
  };

  const allEvtCount = selectedClient ? (events[selectedClient.client_id] || []).length : 0;

  // ── Styles ──
  const tabBtn = (active) => ({
    padding: `${SP[2]} ${SP[4]}`,
    borderRadius: RADIUS.md,
    border: `1.5px solid ${active ? T.accent : T.border}`,
    background: active ? `${T.accent}18` : "transparent",
    color: active ? T.accent : T.sub,
    fontSize: TYPE.xs,
    fontWeight: active ? TYPE.bold : TYPE.medium,
    cursor: "pointer",
    transition: TRANSITION.fast,
  });

  const viewTabs = [
    { id: "pipeline", label: "📊 Pipeline", count: clients.length },
    { id: "timeline", label: "📅 Timeline", count: null },
    { id: "alerts", label: "🔔 Alertas", count: automationAlerts.length },
  ];

  return (
    <div>
      {/* ── Header bar ── */}
      <div
        style={{
          ...flexBetween,
          marginBottom: SP[4],
          flexWrap: "wrap",
          gap: SP[2],
        }}
      >
        <div style={{ ...flexRow, gap: SP[2] }}>
          {viewTabs.map((tab) => (
            <button key={tab.id} onClick={() => setView(tab.id)} style={tabBtn(view === tab.id)}>
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    background:
                      view === tab.id
                        ? T.accent
                        : tab.id === "alerts" && tab.count > 0
                          ? "#DC2626"
                          : T.muted,
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: RADIUS.full,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ fontSize: TYPE.xs, color: T.muted }}>
          {clients.length} cliente{clients.length !== 1 ? "s" : ""} ·{" "}
          {Object.values(events).reduce((s, arr) => s + arr.length, 0)} eventos
        </div>
      </div>

      {/* ── Views ── */}
      {view === "pipeline" && (
        <PipelineView
          columns={pipelineColumns}
          T={T}
          events={events}
          moveClient={moveClient}
          onSelectClient={(c) => {
            setSelectedClient(c);
            setView("timeline");
          }}
        />
      )}

      {view === "timeline" && (
        <TimelineView
          clients={clients}
          filteredClients={filteredClients}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          clientSearch={clientSearch}
          setClientSearch={setClientSearch}
          events={events}
          filteredEvents={filteredEvents}
          clientStats={clientStats}
          allEvtCount={allEvtCount}
          evtTypeFilter={evtTypeFilter}
          setEvtTypeFilter={setEvtTypeFilter}
          evtPeriodFilter={evtPeriodFilter}
          setEvtPeriodFilter={setEvtPeriodFilter}
          newEvt={newEvt}
          setNewEvt={setNewEvt}
          addEvent={addEvent}
          loading={loading}
          showAddEvent={showAddEvent}
          setShowAddEvent={setShowAddEvent}
          showTemplates={showTemplates}
          setShowTemplates={setShowTemplates}
          applyTemplate={applyTemplate}
          T={T}
        />
      )}

      {view === "alerts" && (
        <AlertsView
          alerts={automationAlerts}
          onSelectClient={(c) => {
            setSelectedClient(c);
            setView("timeline");
          }}
          T={T}
        />
      )}
    </div>
  );
}

AdminCRMPanel.propTypes = {
  clients: PropTypes.array,
  crmEvents: PropTypes.object,
  onInsertCRMEvent: PropTypes.func,
  T: PropTypes.object,
};

// ═══════════════════════════════════════════════════════
// PIPELINE VIEW — Kanban board with enhanced cards
// ═══════════════════════════════════════════════════════
function PipelineView({ columns, T, events, moveClient, onSelectClient }) {
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${PIPELINE_STAGES.length}, 1fr)`,
        gap: SP[3],
        minHeight: 400,
      }}
    >
      {PIPELINE_STAGES.map((stage) => {
        const isOver = dropTarget === stage.id;
        const stageClients = columns[stage.id] || [];
        return (
          <div
            key={stage.id}
            onDragOver={(e) => {
              e.preventDefault();
              setDropTarget(stage.id);
            }}
            onDragLeave={() => setDropTarget(null)}
            onDrop={() => {
              if (dragging) moveClient(dragging, stage.id);
              setDragging(null);
              setDropTarget(null);
            }}
            style={{
              ...flexCol,
              background: isOver ? `${stage.color}12` : T.bg,
              borderRadius: RADIUS.lg,
              border: `2px solid ${isOver ? stage.color : T.border}`,
              transition: TRANSITION.fast,
              minHeight: 300,
            }}
          >
            {/* Column header */}
            <div
              style={{
                ...flexBetween,
                padding: `${SP[3]} ${SP[3]}`,
                borderBottom: `2px solid ${stage.color}30`,
                background: `${stage.color}08`,
              }}
            >
              <div style={{ ...flexRow, gap: SP[2], alignItems: "center" }}>
                <span style={{ fontSize: TYPE.lg }}>{stage.icon}</span>
                <span
                  style={{
                    fontSize: TYPE.xs,
                    fontWeight: TYPE.bold,
                    color: T.text,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {stage.label}
                </span>
              </div>
              <span
                style={{
                  background: `${stage.color}20`,
                  color: stage.color,
                  fontSize: 10,
                  fontWeight: TYPE.bold,
                  borderRadius: RADIUS.full,
                  padding: "2px 8px",
                  minWidth: 20,
                  textAlign: "center",
                }}
              >
                {stageClients.length}
              </span>
            </div>

            {/* Cards */}
            <div style={{ flex: 1, padding: SP[2], overflowY: "auto", maxHeight: 450 }}>
              {stageClients.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: `${SP[6]} ${SP[2]}`,
                    color: T.muted,
                    fontSize: TYPE.xs,
                  }}
                >
                  Nenhum cliente
                </div>
              ) : (
                stageClients.map((c) => {
                  const cEvents = events[c.client_id] || [];
                  const evtCount = cEvents.length;
                  const lastEvt = cEvents[0];
                  const lastDate = lastEvt?.event_date || lastEvt?.data_evento || lastEvt?.data;
                  const daysSince = daysAgo(lastDate);
                  const urgency =
                    daysSince > 90 ? "#DC2626" : daysSince > 60 ? "#D97706" : "transparent";

                  return (
                    <div
                      key={c.client_id}
                      draggable
                      onDragStart={() => setDragging(c.client_id)}
                      onDragEnd={() => {
                        setDragging(null);
                        setDropTarget(null);
                      }}
                      onClick={() => onSelectClient(c)}
                      style={{
                        ...cardBase(T),
                        padding: `${SP[2]} ${SP[3]}`,
                        marginBottom: SP[2],
                        cursor: "grab",
                        opacity: dragging === c.client_id ? 0.5 : 1,
                        transition: TRANSITION.fast,
                        borderLeft: `3px solid ${stage.color}`,
                        borderRight: urgency !== "transparent" ? `2px solid ${urgency}` : "none",
                      }}
                    >
                      <div
                        style={{
                          ...flexBetween,
                          marginBottom: 2,
                        }}
                      >
                        <div
                          style={{
                            fontSize: TYPE.xs,
                            fontWeight: TYPE.semibold,
                            color: T.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {c.full_name || c.client_id}
                        </div>
                        {daysSince <= 365 && daysSince !== Infinity && (
                          <span
                            style={{
                              fontSize: 9,
                              color:
                                daysSince > 90 ? "#DC2626" : daysSince > 60 ? "#D97706" : T.muted,
                              fontWeight: 600,
                              marginLeft: 4,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {relativeDate(lastDate)}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: T.muted }}>
                        {evtCount > 0
                          ? `${evtCount} evento${evtCount !== 1 ? "s" : ""}`
                          : "Sem eventos"}
                      </div>
                      {lastEvt && (
                        <div
                          style={{
                            fontSize: 10,
                            color: T.sub,
                            marginTop: 3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {
                            (EVENT_TYPES[lastEvt.tipo || lastEvt.tipo_evento] || EVENT_TYPES.nota)
                              .icon
                          }{" "}
                          {lastEvt.descricao?.slice(0, 40) || "Sem descrição"}
                          {(lastEvt.descricao?.length || 0) > 40 ? "…" : ""}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

PipelineView.propTypes = {
  columns: PropTypes.object.isRequired,
  T: PropTypes.object.isRequired,
  events: PropTypes.object.isRequired,
  moveClient: PropTypes.func.isRequired,
  onSelectClient: PropTypes.func.isRequired,
};

// ═══════════════════════════════════════════════════════
// ALERTS VIEW — Automation & follow-up reminders
// ═══════════════════════════════════════════════════════
function AlertsView({ alerts, onSelectClient, T }) {
  if (alerts.length === 0) {
    return (
      <div
        style={{
          ...cardBase(T),
          padding: "48px 24px",
          textAlign: "center",
        }}
      >
        <p style={{ fontSize: 32, marginBottom: 8 }}>✅</p>
        <p style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
          Nenhum alerta pendente
        </p>
        <p style={{ fontSize: 12, color: T.muted }}>Todos os clientes estão com contato recente</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {[
          {
            label: "Sem Contato (+90d)",
            count: alerts.filter((a) => a.severity === "critical").length,
            color: "#DC2626",
            bg: "#FEE2E2",
          },
          {
            label: "Follow-up (+60d)",
            count: alerts.filter((a) => a.severity === "warning").length,
            color: "#D97706",
            bg: "#FEF3C7",
          },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              ...cardBase(T),
              padding: "14px 18px",
              borderLeft: `3px solid ${s.color}`,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: T.sub,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                marginBottom: 4,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.count}</div>
          </div>
        ))}
      </div>

      {/* Alert list */}
      <div style={{ ...cardBase(T), padding: 0, overflow: "hidden" }}>
        {alerts.map((alert, i) => (
          <button
            key={`${alert.client.client_id}_${i}`}
            onClick={() => onSelectClient(alert.client)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 18px",
              width: "100%",
              border: "none",
              borderBottom: i < alerts.length - 1 ? `1px solid ${T.border}` : "none",
              borderLeft: `3px solid ${alert.severity === "critical" ? "#DC2626" : "#D97706"}`,
              background: i % 2 === 0 ? T.row1 : T.row2,
              cursor: "pointer",
              textAlign: "left",
              transition: TRANSITION.fast,
            }}
          >
            <span style={{ fontSize: 20 }}>{alert.severity === "critical" ? "🔴" : "🟡"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                {alert.client.full_name || alert.client.client_id}
              </div>
              <div style={{ fontSize: 11, color: T.muted }}>{alert.message}</div>
            </div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: alert.severity === "critical" ? "#DC2626" : "#D97706",
                padding: "4px 10px",
                borderRadius: RADIUS.sm,
                background: alert.severity === "critical" ? "#FEE2E2" : "#FEF3C7",
              }}
            >
              {alert.action} →
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

AlertsView.propTypes = {
  alerts: PropTypes.array.isRequired,
  onSelectClient: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};

// ═══════════════════════════════════════════════════════
// TIMELINE VIEW — Enhanced with stats, templates, rich cards
// ═══════════════════════════════════════════════════════
function TimelineView({
  filteredClients,
  selectedClient,
  setSelectedClient,
  clientSearch,
  setClientSearch,
  events,
  filteredEvents,
  clientStats,
  allEvtCount,
  evtTypeFilter,
  setEvtTypeFilter,
  evtPeriodFilter,
  setEvtPeriodFilter,
  newEvt,
  setNewEvt,
  addEvent,
  loading,
  showAddEvent,
  setShowAddEvent,
  showTemplates,
  setShowTemplates,
  applyTemplate,
  T,
}) {
  const searchRef = useRef(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 18, minHeight: 500 }}>
      {/* ── Client List ── */}
      <div
        style={{
          ...cardBase(T),
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: `1px solid ${T.border}`,
            background: T.accent,
          }}
        >
          <p
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "rgba(255,255,255,0.7)",
              textTransform: "uppercase",
              letterSpacing: 0.8,
              margin: "0 0 6px",
            }}
          >
            Clientes
          </p>
          <input
            ref={searchRef}
            type="text"
            placeholder="Buscar…"
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 10px",
              borderRadius: RADIUS.sm,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              fontSize: 12,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {filteredClients.length === 0 ? (
            <div style={{ padding: 24, color: T.muted, fontSize: 12, textAlign: "center" }}>
              {clientSearch ? "Nenhum resultado" : "Nenhum cliente"}
            </div>
          ) : (
            filteredClients.map((c) => {
              const isActive = selectedClient?.client_id === c.client_id;
              const cEvents = events[c.client_id] || [];
              const evtCount = cEvents.length;
              const lastEvt = cEvents[0];
              const lastDate = lastEvt?.event_date || lastEvt?.data_evento || lastEvt?.data;
              const daysSince = daysAgo(lastDate);

              return (
                <div
                  key={c.client_id}
                  onClick={() => {
                    setSelectedClient(c);
                    setEvtTypeFilter("all");
                    setEvtPeriodFilter("all");
                  }}
                  style={{
                    padding: "10px 14px",
                    borderBottom: `1px solid ${T.border}`,
                    cursor: "pointer",
                    background: isActive ? `${T.accent}15` : "transparent",
                    transition: "background 0.1s",
                    borderLeft: isActive ? `3px solid ${T.accent}` : "3px solid transparent",
                  }}
                >
                  <div style={{ ...flexBetween, alignItems: "center" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: isActive ? 700 : 500,
                          color: T.text,
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.full_name || c.client_id}
                      </p>
                      <p style={{ fontSize: 10, color: T.muted, margin: "1px 0 0" }}>
                        {evtCount > 0
                          ? `${evtCount} eventos · ${relativeDate(lastDate)}`
                          : "Sem eventos"}
                      </p>
                    </div>
                    {daysSince > 90 && (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "#DC2626",
                          flexShrink: 0,
                          marginLeft: 6,
                        }}
                        title="Sem contato há +90 dias"
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div>
        {!selectedClient ? (
          <div
            style={{
              ...cardBase(T),
              padding: 48,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 32, marginBottom: 8 }}>👈</p>
            <p style={{ fontSize: 14, color: T.sub }}>Selecione um cliente para ver a timeline</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* ── Client activity stats ── */}
            {clientStats && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                }}
              >
                {[
                  { label: "Total Eventos", v: clientStats.total, color: T.text },
                  {
                    label: "Último Contato",
                    v: relativeDate(clientStats.lastEventDate),
                    color:
                      clientStats.daysSinceLastContact > 90
                        ? "#DC2626"
                        : clientStats.daysSinceLastContact > 60
                          ? "#D97706"
                          : T.success,
                  },
                  {
                    label: "Primeiro Registro",
                    v: fmtDatePT(clientStats.firstEventDate),
                    color: T.sub,
                  },
                  { label: "Tipos", v: Object.keys(clientStats.typeCounts).length, color: T.info },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      ...cardBase(T),
                      padding: "10px 14px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        color: T.muted,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        marginBottom: 3,
                      }}
                    >
                      {s.label}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: s.color }}>{s.v}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Action bar ── */}
            <div style={{ ...flexRow, gap: 8 }}>
              <button
                onClick={() => {
                  setShowAddEvent(!showAddEvent);
                  setShowTemplates(false);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: RADIUS.md,
                  border: `1px solid ${T.accent}`,
                  background: showAddEvent ? T.accent : "transparent",
                  color: showAddEvent ? "#fff" : T.accent,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: TRANSITION.fast,
                }}
              >
                + Novo Evento
              </button>
              <button
                onClick={() => {
                  setShowTemplates(!showTemplates);
                  setShowAddEvent(false);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: RADIUS.md,
                  border: `1px solid ${T.border}`,
                  background: showTemplates ? T.bg : "transparent",
                  color: T.text,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: TRANSITION.fast,
                }}
              >
                📨 Templates
              </button>
            </div>

            {/* ── Templates panel ── */}
            {showTemplates && (
              <div
                style={{
                  ...cardBase(T),
                  padding: 14,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                {MSG_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    style={{
                      padding: "10px 14px",
                      borderRadius: RADIUS.md,
                      border: `1px solid ${T.border}`,
                      background: T.bg,
                      cursor: "pointer",
                      textAlign: "left",
                      transition: TRANSITION.fast,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 2 }}>
                      {t.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: T.muted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.body.replace("{nome}", "...")}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* ── Add event form ── */}
            {showAddEvent && (
              <div style={{ ...cardBase(T), padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 10 }}>
                  Novo evento – {selectedClient.full_name || selectedClient.client_id}
                </div>
                {/* Type chips */}
                <div style={{ ...flexRow, gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                  {Object.entries(EVENT_TYPES).map(([key, meta]) => (
                    <button
                      key={key}
                      onClick={() => setNewEvt((p) => ({ ...p, tipo: key }))}
                      style={{
                        padding: "4px 10px",
                        borderRadius: RADIUS.full,
                        border: `1.5px solid ${newEvt.tipo === key ? meta.color : T.border}`,
                        background: newEvt.tipo === key ? `${meta.color}15` : "transparent",
                        color: newEvt.tipo === key ? meta.color : T.sub,
                        fontSize: 11,
                        fontWeight: newEvt.tipo === key ? 700 : 400,
                        cursor: "pointer",
                        transition: TRANSITION.fast,
                      }}
                    >
                      {meta.icon} {meta.label}
                    </button>
                  ))}
                </div>
                {/* Description + date */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 130px",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <textarea
                    value={newEvt.descricao}
                    onChange={(e) => setNewEvt((p) => ({ ...p, descricao: e.target.value }))}
                    placeholder="Descreva o evento…"
                    rows={2}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: RADIUS.sm,
                      border: `1.5px solid ${T.border}`,
                      fontSize: 12,
                      color: T.text,
                      background: T.card,
                      resize: "vertical",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                  <input
                    type="date"
                    value={newEvt.data}
                    onChange={(e) => setNewEvt((p) => ({ ...p, data: e.target.value }))}
                    style={{
                      padding: "8px 10px",
                      borderRadius: RADIUS.sm,
                      border: `1.5px solid ${T.border}`,
                      fontSize: 12,
                      color: T.text,
                      background: T.card,
                    }}
                  />
                </div>
                <div style={{ ...flexRow, gap: 8, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => setShowAddEvent(false)}
                    style={{
                      padding: "7px 14px",
                      borderRadius: RADIUS.sm,
                      border: `1px solid ${T.border}`,
                      background: "transparent",
                      color: T.sub,
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={addEvent}
                    disabled={loading || !newEvt.descricao.trim()}
                    style={{
                      padding: "7px 16px",
                      borderRadius: RADIUS.sm,
                      border: "none",
                      background: loading || !newEvt.descricao.trim() ? T.muted : T.accent,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: loading ? "wait" : "pointer",
                    }}
                  >
                    {loading ? "Salvando…" : "Salvar"}
                  </button>
                </div>
              </div>
            )}

            {/* ── Timeline events ── */}
            <div style={{ ...cardBase(T), padding: 16 }}>
              <div
                style={{
                  ...flexBetween,
                  marginBottom: 14,
                  flexWrap: "wrap",
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>
                  Timeline{" "}
                  <span style={{ fontSize: 11, fontWeight: 400, color: T.muted }}>
                    ({allEvtCount} evento{allEvtCount !== 1 ? "s" : ""})
                  </span>
                </div>
                <div style={{ ...flexRow, gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <select
                    value={evtTypeFilter}
                    onChange={(e) => setEvtTypeFilter(e.target.value)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: RADIUS.sm,
                      border: `1px solid ${T.border}`,
                      fontSize: 11,
                      color: T.text,
                      background: T.card,
                      cursor: "pointer",
                    }}
                  >
                    <option value="all">Todos</option>
                    {Object.entries(EVENT_TYPES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ ...flexRow, gap: 2 }}>
                    {PERIOD_OPTS.map((p) => (
                      <button
                        key={p.value}
                        onClick={() => setEvtPeriodFilter(p.value)}
                        style={{
                          padding: "3px 8px",
                          borderRadius: RADIUS.sm,
                          border: `1px solid ${evtPeriodFilter === p.value ? T.accent : T.border}`,
                          background: evtPeriodFilter === p.value ? `${T.accent}12` : "transparent",
                          color: evtPeriodFilter === p.value ? T.accent : T.sub,
                          fontSize: 10,
                          fontWeight: evtPeriodFilter === p.value ? 700 : 400,
                          cursor: "pointer",
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {filteredEvents.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "28px 0",
                    color: T.muted,
                    fontSize: 12,
                  }}
                >
                  {allEvtCount === 0
                    ? "Nenhum evento registrado para este cliente."
                    : "Nenhum evento corresponde aos filtros."}
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: 18,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      background: T.border,
                    }}
                  />
                  {filteredEvents.map((e, idx) => {
                    const tipo = e.tipo || e.tipo_evento || "nota";
                    const meta = EVENT_TYPES[tipo] || EVENT_TYPES.nota;
                    const date = e.event_date || e.data_evento || e.data;
                    return (
                      <div
                        key={e.id || idx}
                        style={{
                          display: "flex",
                          gap: 14,
                          marginBottom: 16,
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: RADIUS.full,
                            background: `${meta.color}18`,
                            border: `2px solid ${meta.color}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 15,
                            flexShrink: 0,
                            zIndex: 1,
                            boxShadow: `0 0 0 3px ${T.card}`,
                          }}
                        >
                          {meta.icon}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            background: T.bg,
                            borderRadius: RADIUS.md,
                            padding: "10px 14px",
                            marginTop: 4,
                          }}
                        >
                          <div
                            style={{
                              ...flexBetween,
                              alignItems: "flex-start",
                              marginBottom: 3,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: meta.color,
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                              }}
                            >
                              {meta.label}
                            </span>
                            <span style={{ fontSize: 10, color: T.muted }}>
                              {fmtDatePT(date)} <span style={{ color: T.border }}>·</span>{" "}
                              {relativeDate(date)}
                            </span>
                          </div>
                          <p style={{ fontSize: 12, color: T.text, margin: 0, lineHeight: 1.5 }}>
                            {e.descricao}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

TimelineView.propTypes = {
  filteredClients: PropTypes.array.isRequired,
  selectedClient: PropTypes.object,
  setSelectedClient: PropTypes.func.isRequired,
  clientSearch: PropTypes.string.isRequired,
  setClientSearch: PropTypes.func.isRequired,
  events: PropTypes.object.isRequired,
  filteredEvents: PropTypes.array.isRequired,
  clientStats: PropTypes.object,
  allEvtCount: PropTypes.number.isRequired,
  evtTypeFilter: PropTypes.string.isRequired,
  setEvtTypeFilter: PropTypes.func.isRequired,
  evtPeriodFilter: PropTypes.string.isRequired,
  setEvtPeriodFilter: PropTypes.func.isRequired,
  newEvt: PropTypes.object.isRequired,
  setNewEvt: PropTypes.func.isRequired,
  addEvent: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  showAddEvent: PropTypes.bool.isRequired,
  setShowAddEvent: PropTypes.func.isRequired,
  showTemplates: PropTypes.bool.isRequired,
  setShowTemplates: PropTypes.func.isRequired,
  applyTemplate: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};
